/**
 * Countries where the Tax Details section is shown and tax identifier is required.
 * Must stay in sync with backend tax identifier validation in wallet/serializers.py.
 * See docs/WALLET_PRODUCTS.md "Fully Supported Currencies" and "Adding a New Country/Currency".
 * Note: AU and US are not in this list because they don't require a tax identifier.
 */
export const COUNTRIES_REQUIRING_TAX_IDENTIFIER: string[] = [
  'GB',
  'NZ',
  'CH',
  'ZA',
  'IN',
  'PK',
  'SE',
  'DK',
  'PL',
  'HU',
  'CZ',
  'RO',
  'JP',
  // EU (EUR) countries
  'AT',
  'BE',
  'BG',
  'CY',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HR',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PT',
  'SK',
  'SI',
  'ES',
  'CA',
]

/**
 * Org country → wallet billing currency (ISO 4217). Keep in sync with
 * django/osapi/constants.py COUNTRY_CURRENCY.
 */
export const COUNTRY_ISO2_TO_WALLET_CURRENCY: Record<string, string> = {
  AU: 'AUD',
  US: 'USD',
  GB: 'GBP',
  AT: 'EUR',
  BE: 'EUR',
  BG: 'EUR',
  HR: 'EUR',
  CY: 'EUR',
  EE: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  ES: 'EUR',
  ML: 'XOF',
  NE: 'XOF',
  SN: 'XOF',
  TG: 'XOF',
  GW: 'XOF',
  CI: 'XOF',
  BJ: 'XOF',
  BF: 'XOF',
  AO: 'AOA',
  NZ: 'NZD',
  SE: 'SEK',
  CZ: 'CZK',
  DK: 'DKK',
  HU: 'HUF',
  PL: 'PLN',
  RO: 'RON',
  CH: 'CHF',
  ZA: 'ZAR',
  IN: 'INR',
  JP: 'JPY',
  PK: 'PKR',
  CA: 'CAD',
}

export function walletCurrencyFromCountryIso2(iso2: string | undefined | null): string {
  if (!iso2) return 'USD'
  const code = iso2.toString().toUpperCase()
  return COUNTRY_ISO2_TO_WALLET_CURRENCY[code] ?? 'USD'
}

export const TOP_UP_AMT_CHOICES: Record<string, { id: number; name: string }[]> = {
  USD: [
    { id: 100, name: '$100' },
    { id: 500, name: '$500' },
    { id: 1000, name: '$1,000' },
  ],
  AUD: [
    { id: 100, name: '$100' },
    { id: 500, name: '$500' },
    { id: 1000, name: '$1,000' },
  ],
  NZD: [
    { id: 100, name: '$100' },
    { id: 500, name: '$500' },
    { id: 1000, name: '$1,000' },
  ],
  CAD: [
    { id: 100, name: '$100' },
    { id: 500, name: '$500' },
    { id: 1000, name: '$1,000' },
  ],
  GBP: [
    { id: 100, name: '£100' },
    { id: 500, name: '£500' },
    { id: 1000, name: '£1,000' },
  ],
  EUR: [
    { id: 100, name: '€100' },
    { id: 500, name: '€500' },
    { id: 1000, name: '€1,000' },
  ],
  CHF: [
    { id: 100, name: 'CHF 100' },
    { id: 500, name: 'CHF 500' },
    { id: 1000, name: 'CHF 1,000' },
  ],
  ZAR: [
    { id: 500, name: 'R500' },
    { id: 2000, name: 'R2,000' },
    { id: 5000, name: 'R5,000' },
  ],
  INR: [
    { id: 5000, name: '₹5,000' },
    { id: 20000, name: '₹20,000' },
    { id: 50000, name: '₹50,000' },
  ],
  PKR: [
    { id: 10000, name: 'Rs 10,000' },
    { id: 50000, name: 'Rs 50,000' },
    { id: 100000, name: 'Rs 100,000' },
  ],
  PHP: [
    { id: 5000, name: '₱5,000' },
    { id: 20000, name: '₱20,000' },
    { id: 50000, name: '₱50,000' },
  ],
  SEK: [
    { id: 1000, name: '1 000 kr' },
    { id: 5000, name: '5 000 kr' },
    { id: 10000, name: '10 000 kr' },
  ],
  DKK: [
    { id: 500, name: '500 kr' },
    { id: 2000, name: '2 000 kr' },
    { id: 5000, name: '5 000 kr' },
  ],
  PLN: [
    { id: 500, name: '500 zł' },
    { id: 2000, name: '2 000 zł' },
    { id: 5000, name: '5 000 zł' },
  ],
  HUF: [
    { id: 50000, name: '50 000 Ft' },
    { id: 100000, name: '100 000 Ft' },
    { id: 200000, name: '200 000 Ft' },
  ],
  CZK: [
    { id: 2000, name: '2 000 Kč' },
    { id: 5000, name: '5 000 Kč' },
    { id: 10000, name: '10 000 Kč' },
  ],
  RON: [
    { id: 500, name: '500 lei' },
    { id: 2000, name: '2 000 lei' },
    { id: 5000, name: '5 000 lei' },
  ],
  JPY: [
    { id: 10000, name: '¥10,000' },
    { id: 50000, name: '¥50,000' },
    { id: 100000, name: '¥100,000' },
  ],
  default: [
    { id: 100, name: '$100' },
    { id: 500, name: '$500' },
    { id: 1000, name: '$1,000' },
  ],
}

export const getTopUpAmtChoices = (countryCode: string) => {
  if (!countryCode || !TOP_UP_AMT_CHOICES[countryCode]) return TOP_UP_AMT_CHOICES['default']
  else return TOP_UP_AMT_CHOICES[countryCode]
}

export const TOP_UP_TRIGGER_CHOICES: Record<string, { id: number; name: string }[]> = {
  USD: [
    { id: 20, name: '$20' },
    { id: 100, name: '$100' },
  ],
  AUD: [
    { id: 20, name: '$20' },
    { id: 100, name: '$100' },
  ],
  NZD: [
    { id: 20, name: '$20' },
    { id: 100, name: '$100' },
  ],
  CAD: [
    { id: 20, name: '$20' },
    { id: 100, name: '$100' },
  ],
  GBP: [
    { id: 20, name: '£20' },
    { id: 100, name: '£100' },
  ],
  EUR: [
    { id: 20, name: '€20' },
    { id: 100, name: '€100' },
  ],
  CHF: [
    { id: 20, name: 'CHF 20' },
    { id: 100, name: 'CHF 100' },
  ],
  ZAR: [
    { id: 100, name: 'R100' },
    { id: 500, name: 'R500' },
  ],
  INR: [
    { id: 1000, name: '₹1,000' },
    { id: 5000, name: '₹5,000' },
  ],
  PKR: [
    { id: 5000, name: 'Rs 5,000' },
    { id: 20000, name: 'Rs 20,000' },
  ],
  PHP: [
    { id: 1000, name: '₱1,000' },
    { id: 5000, name: '₱5,000' },
  ],
  SEK: [
    { id: 200, name: '200 kr' },
    { id: 1000, name: '1 000 kr' },
  ],
  DKK: [
    { id: 100, name: '100 kr' },
    { id: 500, name: '500 kr' },
  ],
  PLN: [
    { id: 100, name: '100 zł' },
    { id: 500, name: '500 zł' },
  ],
  HUF: [
    { id: 5000, name: '5 000 Ft' },
    { id: 20000, name: '20 000 Ft' },
  ],
  CZK: [
    { id: 500, name: '500 Kč' },
    { id: 2000, name: '2 000 Kč' },
  ],
  RON: [
    { id: 100, name: '100 lei' },
    { id: 500, name: '500 lei' },
  ],
  JPY: [
    { id: 2000, name: '¥2,000' },
    { id: 10000, name: '¥10,000' },
  ],
  default: [
    { id: 20, name: '$20' },
    { id: 100, name: '$100' },
  ],
}

export const getTopUpTriggerChoices = (countryCode: string) => {
  if (!countryCode || !TOP_UP_TRIGGER_CHOICES[countryCode]) return TOP_UP_TRIGGER_CHOICES['default']
  else return TOP_UP_TRIGGER_CHOICES[countryCode]
}
